import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './AdminLayout.css';
import { Layout, Menu, Input, Avatar, Dropdown, message, Badge } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  PlusCircleOutlined,
  AppstoreOutlined,
  ReadOutlined,
  PictureOutlined,
  SearchOutlined,
  SettingOutlined,
  KeyOutlined,
  UnlockOutlined,
  LogoutOutlined,
  UserOutlined,
  BellOutlined,
} from '@ant-design/icons';

const { Header, Sider, Content } = Layout;

const AdminLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearch = (value) => {
    if (!value.trim()) {
      message.warning('Veuillez saisir un terme de recherche');
      return;
    }
    navigate(`/admin/products?search=${encodeURIComponent(value.trim())}`);
  };

  const selectedKey = (() => {
    const p = location.pathname;
    if (p.startsWith('/admin/products/new')) return '/admin/products/new';
    if (p.startsWith('/admin/products')) return '/admin/products';
    if (p.startsWith('/admin/categories/new')) return '/admin/categories/new';
    if (p.startsWith('/admin/categories')) return '/admin/categories';
    if (p.startsWith('/admin/blogs/new')) return '/admin/blogs/new';
    if (p.startsWith('/admin/blogs')) return '/admin/blogs';
    if (p.startsWith('/admin/advertisements')) return '/admin/advertisements';
    if (p.startsWith('/admin/settings')) return '/admin/settings';
    if (p.startsWith('/admin/admin-pin')) return '/admin/admin-pin';
    return '/admin/dashboard';
  })();

  const openKeys = (() => {
    const p = location.pathname;
    const keys = [];
    if (p.startsWith('/admin/products')) keys.push('products');
    if (p.startsWith('/admin/categories')) keys.push('categories');
    if (p.startsWith('/admin/blogs')) keys.push('blogs');
    if (p.startsWith('/admin/settings') || p.startsWith('/admin/admin-pin')) keys.push('settings-group');
    return keys;
  })();

  const menuItems = [
    {
      key: '/admin/dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/admin/dashboard">Tableau de bord</Link>,
    },
    {
      key: 'products',
      icon: <ShoppingOutlined />,
      label: 'Produits',
      children: [
        {
          key: '/admin/products/new',
          icon: <PlusCircleOutlined />,
          label: <Link to="/admin/products/new">Nouveau produit</Link>,
        },
        {
          key: '/admin/products',
          icon: <ShoppingOutlined />,
          label: <Link to="/admin/products">Liste produits</Link>,
        },
      ],
    },
    {
      key: 'categories',
      icon: <AppstoreOutlined />,
      label: 'Catégories',
      children: [
        {
          key: '/admin/categories/new',
          icon: <PlusCircleOutlined />,
          label: <Link to="/admin/categories/new">Nouvelle catégorie</Link>,
        },
        {
          key: '/admin/categories',
          icon: <AppstoreOutlined />,
          label: <Link to="/admin/categories">Liste catégories</Link>,
        },
      ],
    },
    {
      key: 'blogs',
      icon: <ReadOutlined />,
      label: 'Blog',
      children: [
        {
          key: '/admin/blogs/new',
          icon: <PlusCircleOutlined />,
          label: <Link to="/admin/blogs/new">Nouvel article</Link>,
        },
        {
          key: '/admin/blogs',
          icon: <ReadOutlined />,
          label: <Link to="/admin/blogs">Liste des articles</Link>,
        },
      ],
    },
    {
      key: '/admin/advertisements',
      icon: <PictureOutlined />,
      label: <Link to="/admin/advertisements">Publicités</Link>,
    },
    {
      key: 'settings-group',
      icon: <SettingOutlined />,
      label: 'Paramètres',
      children: [
        {
          key: '/admin/settings',
          icon: <KeyOutlined />,
          label: <Link to="/admin/settings">Paramètres du compte</Link>,
        },
        {
          key: '/admin/admin-pin',
          icon: <UnlockOutlined />,
          label: <Link to="/admin/admin-pin">Code PIN</Link>,
        },
      ],
    },
  ];

  const userInitials = (user?.username || 'A').slice(0, 2).toUpperCase();

  return (
    <Layout className="admin-shell">
      <Sider
        width={250}
        collapsed={collapsed}
        collapsedWidth={80}
        className="admin-sider"
        trigger={null}
      >
        <div className="admin-sider__brand">
          <Link to="/admin/dashboard" className="admin-sider__logo">
            <img src="/images/LOGO.png" alt="DOMOELEC" />
            {!collapsed && (
              <div className="admin-sider__brand-text">
                <span className="admin-sider__brand-name">DOMOELEC</span>
                <span className="admin-sider__brand-sub">Administration</span>
              </div>
            )}
          </Link>
        </div>

        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={openKeys}
          items={menuItems}
          className="admin-sider__menu"
        />

        {!collapsed && (
          <div className="admin-sider__footer">
            <div className="admin-sider__footer-dot" />
            <span>DOMOELEC © {new Date().getFullYear()}</span>
          </div>
        )}
      </Sider>

      <Layout className="admin-body">
        <Header className="admin-header">
          <div className="admin-header__left">
            <button
              type="button"
              className="admin-header__collapse"
              onClick={() => setCollapsed(!collapsed)}
              aria-label="Réduire le menu"
            >
              <i className={`fas ${collapsed ? 'fa-bars' : 'fa-angle-left'}`} />
            </button>
            <Input
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Rechercher un produit..."
              allowClear
              className="admin-header__search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={(e) => handleSearch(e.target.value)}
            />
          </div>

          <div className="admin-header__right">
            <button type="button" className="admin-header__icon-btn" aria-label="Notifications">
              <Badge dot color="#10b981">
                <BellOutlined style={{ fontSize: 18 }} />
              </Badge>
            </button>

            <Dropdown
              placement="bottomRight"
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'profile',
                    icon: <UserOutlined />,
                    label: (
                      <div style={{ padding: '4px 0' }}>
                        <div style={{ fontWeight: 600 }}>{user?.username}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {user?.role === 'admin' ? 'Administrateur' : 'Éditeur'}
                        </div>
                      </div>
                    ),
                    disabled: true,
                  },
                  { type: 'divider' },
                  {
                    key: 'settings',
                    icon: <KeyOutlined />,
                    label: 'Changer le mot de passe',
                    onClick: () => navigate('/admin/settings'),
                  },
                  {
                    key: 'pin',
                    icon: <UnlockOutlined />,
                    label: 'Code PIN',
                    onClick: () => navigate('/admin/admin-pin'),
                  },
                  { type: 'divider' },
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: 'Déconnexion',
                    danger: true,
                    onClick: handleLogout,
                  },
                ],
              }}
            >
              <div className="admin-header__user">
                <Avatar className="admin-header__avatar">{userInitials}</Avatar>
                <div className="admin-header__user-info">
                  <span className="admin-header__user-name">{user?.username}</span>
                  <span className="admin-header__user-role">
                    {user?.role === 'admin' ? 'Administrateur' : 'Éditeur'}
                  </span>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="admin-content">{children}</Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
